import { getErrorMessage } from './errorUtils';
import { logError, logTrace, logEvent } from './trackingUtils';
import { signOut } from 'aws-amplify/auth';

/**
 * Networking/auth utilities for API calls.
 *
 * Responsibilities:
 * - Perform authenticated fetch requests.
 * - Validate responses and enforce consistent error handling.
 * - Handle 401 Unauthorized by signing out and redirecting to login.
 */

/**
 * Executes an authenticated fetch call and validates the response.
 *
 * Notes:
 * - On 401, this will sign the user out and redirect to `/login?expired=true`,
 *   then throw an Error.
 * - On other non-OK statuses, this throws an Error containing a best-effort
 *   parsed message from the response body.
 *
 * @param url - Request URL.
 * @param accessToken - Bearer token used for Authorization header.
 * @param method - HTTP method.
 * @param postData - Optional JSON-serializable payload.
 * @returns The fetch Response (even for dev override; validation may throw).
 * @throws Error when response is unauthorized or not ok.
 */
export async function call(
  url: string,
  accessToken: string,
  method: string,
  postData?: object
): Promise<Response> {
  const response = await fetch(url, {
    method,
    body: postData ? JSON.stringify(postData) : undefined,
    headers: buildAuthHeaders(accessToken),
  });

  await validateResponse(url, response);
  return response;
}

/**
 * Validates an HTTP response and throws appropriate errors.
 *
 * Behavior:
 * - 401: Logs, signs the user out (global first, then local fallback),
 *   redirects to login with `expired=true`, then throws.
 * - Non-OK: Attempts to parse and throw a meaningful error message.
 * - OK: Returns without error.
 *
 * @param requestUrl - The request URL used for logging.
 * @param response - The fetch Response to validate.
 * @throws Error when the response indicates an error status.
 */
export async function validateResponse(
  requestUrl: string,
  response: Response
): Promise<void> {
  if (response.status === 401) {
    console.error(`401 Unauthorized response from api at [${requestUrl}].`);
    logTrace('Auth', 'unauthorized_401', requestUrl);

    await handleUnauthorized();
    throw new Error('Authorization error.');
  }

  if (!response.ok) {
    console.error(
      `Error status from api at [${requestUrl}]: [${response.status}].`
    );
    logTrace('API', 'non_ok_status', `${requestUrl} [${response.status}]`);

    throw new Error(await getSafeErrorMessage(response));
  }
}

/**
 * Builds standard auth headers for JSON API requests.
 *
 * @param accessToken - Bearer token used for Authorization header.
 * @returns HeadersInit suitable for fetch.
 */
function buildAuthHeaders(accessToken: string): HeadersInit {
  return {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Signs the user out (global first, then local fallback) and redirects to login.
 *
 * This mirrors the sequencing used in the Reports UI so credentials are cleared
 * before navigation.
 */
async function handleUnauthorized(): Promise<void> {
  try {
    await signOut({ global: true });
  } catch (error) {
    logError('auth.signout.global_failed', error as unknown);
    console.warn(
      'Global sign out failed during 401 handling; falling back to local sign out:',
      error
    );
    try {
      await signOut();
    } catch {
      /* noop */
    }
  }

  redirectToLogin();
}

/**
 * Hard redirects to the login route with an `expired=true` indicator.
 *
 * Uses a hard redirect to avoid coupling to any router implementation.
 * Errors are swallowed and reported via telemetry.
 */
function redirectToLogin(): void {
  try {
    if (typeof window !== 'undefined' && window.location) {
      logTrace('Auth', 'redirect_to_login', '/login?expired=true');
      logEvent({ action: 'auth.redirect_to_login', reason: 'expired' });
      window.location.replace('/login?expired=true');
    }
  } catch (error) {
    logError('auth.redirect_to_login_failed', error as unknown);
  }
}

/**
 * Best-effort extraction of an error message from a non-OK response.
 *
 * @param response - The Response to parse.
 * @returns A human-friendly message if parsing succeeds, otherwise a fallback.
 */
async function getSafeErrorMessage(response: Response): Promise<string> {
  try {
    return (
      (await getErrorMessage(response)) ??
      `Response indicated error: ${response.status}`
    );
  } catch {
    return `Response indicated error: ${response.status}`;
  }
}
